import {
  AddOutlined,
  AddRounded,
  EditOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { Button, Box } from "@mui/material";
import { isEmpty, size } from "lodash";
import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { columnTypesDate } from "../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { formatDateShort } from "../../../utils/dates/formatDateShort";
import SearchInput from "../../inputs/SearchInput";
import Flx from "../../layout/Flx";
import AgGridTableWrapper from "../../agGrid/AgGridTableWrapper";
import BasicModal from "../../modals/BasicModal";
import { grey } from "@mui/material/colors";
import { is } from "immutable";

const RffImportBorrowerDocumentsFromReduxButton = ({
  children,
  /** (Optional) Array of docGroups to filter */
  docGroups = [],
  /** (Optional) Array of docTypes to filter */
  docTypes = [],
}) => {
  const keyName = "borrowerDocuments";
  const [show, setShow] = useState(false);
  const form = useForm();
  const { values } = useFormState();
  const profile = useSelector((state) => state?.profile);
  const borrowerDocuments = profile?.borrowerDocuments || [];

  /**
   * Filter borrower documents based on docGroups and docTypes
   * If both are empty, show all documents
   * If either is provided, show documents matching those criteria
   */
  const filteredBorrowerDocuments = useMemo(() => {
    return borrowerDocuments;
    if (isEmpty(docGroups) && isEmpty(docTypes)) {
      return borrowerDocuments;
    }

    return borrowerDocuments.filter((doc) => {
      const matchesGroup =
        isEmpty(docGroups) || docGroups.includes(doc.docGroup);
      const matchesType = isEmpty(docTypes) || docTypes.includes(doc.docType);
      return matchesGroup && matchesType;
    });
  }, [borrowerDocuments, docGroups, docTypes]);

  /**
   * Get currently imported documents that match the filter criteria
   */
  const currentlyImportedDocs = useMemo(() => {
    const importedDocs = values?.borrowerDocuments || [];

    if (isEmpty(docGroups) && isEmpty(docTypes)) {
      return importedDocs;
    }

    return importedDocs.filter((doc) => {
      const matchesGroup =
        isEmpty(docGroups) || docGroups.includes(doc.docGroup);
      const matchesType = isEmpty(docTypes) || docTypes.includes(doc.docType);
      return matchesGroup && matchesType;
    });
  }, [values?.borrowerDocuments, docGroups, docTypes]);

  const handleImport = () => {
    setShow(true);
  };

  const onAddToForm = (selectedFiles) => {
    // Get current documents from Final Form
    const currentDocuments = form.getState().values[keyName] || [];

    // Get the IDs of documents we're about to add
    const selectedIds = selectedFiles.map((file) => file._id || file.oid);

    // Remove any existing documents that match the selected IDs (to avoid duplicates)
    const filteredCurrentDocs = currentDocuments.filter((doc) => {
      const docId = doc._id || doc.oid;
      return !selectedIds.includes(docId);
    });

    // Merge selected files with current documents
    const updatedDocuments = [...filteredCurrentDocs, ...selectedFiles];

    // Update Final Form
    form.change(keyName, updatedDocuments);

    // Close modal
    setShow(false);
  };

  if (isEmpty(filteredBorrowerDocuments)) {
    return null;
  }

  return (
    <>
      <Button
        variant="text"
        color="inherit"
        onClick={handleImport}
        sx={{
          background: grey[200],
          px: 1,
          //

          border: `1px solid #e0e0e0a8`,
          // border: `1px solid ${grey[300]}`,
          "&:hover": {
            background: grey[300],
          },

          ".MuiSvgIcon-root": {
            fontSize: "16px",
          },
        }}
        startIcon={
          isEmpty(currentlyImportedDocs) ? <AddRounded /> : <EditOutlined />
        }
      >
        {isEmpty(currentlyImportedDocs)
          ? "Add from Library"
          : `Modify Imported Documents (${size(currentlyImportedDocs)})`}
      </Button>
      <BasicModal
        show={show}
        maxWidth="lg"
        suppressBodyPadding
        onClose={() => setShow(false)}
        title="Select Documents From Library"
      >
        <UploadedFilesTable
          files={filteredBorrowerDocuments}
          onAddToForm={onAddToForm}
          preselectedDocs={currentlyImportedDocs}
        />
      </BasicModal>
    </>
  );
};

const UploadedFilesTable = ({ files, onAddToForm, preselectedDocs = [] }) => {
  const ref = useRef(null);
  const [selected, setSelected] = useState([]);
  const [quickFilter, setQuickFilter] = useState(null);
  const { values } = useFormState();
  /**
   * Helper function to get the ID from a document object
   */
  const getDocId = (doc) => {
    return doc._id || doc.oid;
  };

  /**
   * Preselect rows on grid ready and when preselectedDocs changes
   */
  useEffect(() => {
    if (!ref.current || isEmpty(preselectedDocs)) return;

    const gridApi = ref.current.api;
    if (!gridApi) return;

    // Get IDs of preselected documents
    const preselectedIds = preselectedDocs.map(getDocId);

    // Select rows that match preselected IDs
    gridApi.forEachNode((node) => {
      const nodeId = getDocId(node.data);
      if (preselectedIds.includes(nodeId)) {
        node.setSelected(true);
      }
    });
  }, [preselectedDocs]);

  const handleRowSelectionChange = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelected(selectedRows);
  }, []);

  const handleRowClick = useCallback((params) => {
    // Toggle selection on row click
    params.node.setSelected(!params.node.isSelected());
  }, []);

  const handleAddToForm = useCallback(() => {
    onAddToForm(selected);
  }, [selected, onAddToForm]);

  const handleGridReady = useCallback(
    (params) => {
      if (isEmpty(preselectedDocs)) return;

      // Get IDs of preselected documents
      const preselectedIds = preselectedDocs.map(getDocId);

      // Select rows that match preselected IDs
      params.api.forEachNode((node) => {
        const nodeId = getDocId(node.data);
        if (preselectedIds.includes(nodeId)) {
          node.setSelected(true);
        }
      });
    },
    [preselectedDocs],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 150,
        sort: "asc",
      },
      {
        headerName: "Document Group",
        field: "docGroup",
        width: 220,
        minWidth: 150,
      },
      {
        headerName: "Document Type",
        field: "docType",
        width: 220,
        minWidth: 150,
      },
      {
        headerName: "Upload Date",
        field: "uploadDate",
        valueFormatter: (params) => {
          return formatDateShort(params.value);
        },
        flex: 0,
        minWidth: 150,
      },
    ],
    [],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
    };
  }, []);

  if (isEmpty(files)) {
    return null;
  }

  return (
    <Flx column sx={{ p: 0 }}>
      <Flx
        wrap
        fw
        jb
        ac
        gap={2}
        sx={{
          p: 2,
          background: grey[100],
          borderBottom: "1px solid #e0e0e0",
          borderTop: "1px solid #e0e0e0",
          position: "sticky",
          top: 0,
          left: 0,
          zIndex: 2211,
        }}
      >
        <SearchInput
          fullWidth
          value={quickFilter}
          sx={{ flexBasis: "500px", flexGrow: 1, background: "#ffffff" }}
          onChange={(e) => setQuickFilter(e)}
        />
        <Button
          onClick={handleAddToForm}
          variant="contained"
          size="large"
          disabled={isEmpty(values?.borrowerDocuments) && isEmpty(selected)}
          startIcon={
            isEmpty(values?.borrowerDocuments) ? <AddOutlined /> : null
          }
        >
          {isEmpty(values?.borrowerDocuments)
            ? `Add To Application (${size(selected)})`
            : `Update Imported Documents (${size(selected)})`}
        </Button>
      </Flx>
      <AgGridTableWrapper simpleTable suppressMinHeightWhenPopulated={files}>
        <AgGridReact
          ref={ref}
          rowData={files}
          columnDefs={columnDefs}
          singleClickEdit
          quickFilterText={quickFilter}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          onSelectionChanged={handleRowSelectionChange}
          onRowClicked={handleRowClick}
          onGridReady={handleGridReady}
          rowSelection={{
            mode: "multiRow",
          }}
        />
      </AgGridTableWrapper>
    </Flx>
  );
};

export default RffImportBorrowerDocumentsFromReduxButton;
